// MARK: NPM Modules
import React from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import Gist from "react-gist";
// MARK: Redux
import { RootState } from "reduxFunctionality/index";
import { useSelector } from "react-redux";
// MARK: Types
import ReduxStyleguide from "types/redux/styleguide";
import ButtonType from "types/fields/matrix/common/button";
import SectionTitle, {
    Copy,
    Gist as GistType,
    Markdown as MarkdownType,
    Media as MediaType,
    SectionSubtitle,
    SectionSubSubtitle,
    Quote,
  } from "types/fields/matrix/blog/blogContentBlocks";
import BlogContentBlocksType from "types/fragments/blog/blogContentBlocks";
// MARK: Components
import BlogPostHeader from "./blogPostHeader";
import Typography from "components/common/typography";
import TextButton from "components/common/button/textButton";
import Media from "utils/common/media";
// MARK: Utils
import CSSStyles from "enums/cssStyles";


// MARK: Styled Components
interface WrapperProps {
    maxWidth?: number;
    marginTop: number;
    height: string;
  }
  
  const Wrapper = styled.div<WrapperProps>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: ${({ height }) => height};
    margin-top: ${({ marginTop }) => marginTop}px !important;
    margin: auto;
    max-width: ${({ maxWidth }) => (maxWidth ? maxWidth + "px" : "")};
    border-radius: 0px;
    background-color: transparent;
    overflow: hidden;
  `;
  
  interface ButtonContainerProps {
    margin: number;
  }
  
  const ButtonContainer = styled.div<ButtonContainerProps>`
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    margin-top: ${({ margin }) => margin}px;
  `;
  
  const Markdown = styled(({ ...props }) => <ReactMarkdown {...props} />)`
    white-space: pre-wrap;
    line-height: 1;
  `;


// Styleguide & Datapoints
const getDataPoints = (styleguide: ReduxStyleguide) => {
    const { colors } = styleguide;
    const { primary, secondary, background } = colors;
    return {
      sectionTitleStyle: CSSStyles.sectionTitle,
      sectionTitleColor: secondary,
      sectionSubTitleStyle: CSSStyles.medium,
      sectionSubTitleColor: secondary,
      sectionSubSubTitleStyle: CSSStyles.sectionSubtitle,
      sectionSubSubTitleColor: secondary,
      quoteStyle: CSSStyles.navigation,
      quoteColor: primary,
      sourceStyle: CSSStyles.navigation,
      sourceColor: secondary,
      copyStyle: CSSStyles.small,
      copyColor: primary,
      mediaSubtitleStyle: CSSStyles.bodyBold,
      mediaSubtitleColor: secondary,
      background: background,
    };
  };

interface BlogContentBlocksProps {
    blogContentBlocks: BlogContentBlocksType
    copiedURLLink: (() => void)
}


const BlogContentBlocks = ({blogContentBlocks, copiedURLLink} : BlogContentBlocksProps) => {
    const styleguide: ReduxStyleguide = useSelector(
        (state: RootState) => state.styleguide
    );

    const {
        containerWidth,
        maximumBlogWidth,
        margin,
        gutter,
      } = styleguide;
    const layoutWidth =
        containerWidth > maximumBlogWidth ? maximumBlogWidth : containerWidth;
    const imageWidth =
        layoutWidth > containerWidth
          ? containerWidth
          : layoutWidth + (containerWidth - layoutWidth) / 2;
    const {
        sectionTitleStyle,
        sectionTitleColor,
        sectionSubTitleStyle,
        sectionSubTitleColor,
        sectionSubSubTitleStyle,
        sectionSubSubTitleColor,
        quoteStyle,
        quoteColor,
        sourceStyle,
        sourceColor,
        copyStyle,
        copyColor,
        mediaSubtitleStyle,
        mediaSubtitleColor,
        background,
      } = getDataPoints(styleguide);

    var items: Array<any> = [];
    blogContentBlocks.forEach((item, index) => {
      switch (item.typeHandle) {
        case "sectionTitle":
          const sectionTitle = item as SectionTitle;
          const sectionTitleId = sectionTitle.copy
            .toLowerCase()
            .replace(" ", "-")
            .replace(/\s/g, "-")
            .replace("&amp;", "")
            .replace(" ", "-")
            .replace("<p>", "")
            .replace("</p>", "");
          // items.push(
          //   <Typography
          //     type="h2"
          //     key={index}
          //     id={sectionTitleId}
          //     marginTop={margin}
          //     margin={"auto"}
          //     maxwidth={layoutWidth}
          //     className={sectionTitleStyle}
          //     color={sectionTitleColor}
          //     dangerouslySetInnerHTML
          //   >
          //     {sectionTitle.copy}
          //   </Typography>
          // );
          items.push(
            <BlogPostHeader
              key={index}
              id={sectionTitleId}
              className={sectionTitleStyle}
              color={sectionTitleColor}
              background={background}
              headerType="h2"
              copy={sectionTitle.copy}
              layoutWidth={layoutWidth}
              onClick={() => {
                copiedURLLink();
              }}
            />
          );
          break;
        case "sectionSubTitle":
          const sectionSubTitle = item as SectionSubtitle;
          const sectionSubTitleId = sectionSubTitle.copy
            .toLowerCase()
            .replace(" ", "-")
            .replace(/\s/g, "-")
            .replace("&amp;", "")
            .replace("<p>", "")
            .replace("</p>", "");
          items.push(
            <BlogPostHeader
              key={index}
              id={sectionSubTitleId}
              className={sectionSubTitleStyle}
              color={sectionSubTitleColor}
              background={background}
              headerType="h3"
              copy={sectionSubTitle.copy}
              layoutWidth={layoutWidth}
              onClick={() => {
                copiedURLLink();
              }}
            />
          );
          break;
        case "sectionSubSubTitle":
          const sectionSubSubTitle = item as SectionSubSubtitle;
          const sectionSubSubTitleId = sectionSubSubTitle.copy
            .toLowerCase()
            .replace(" ", "-")
            .replace(/\s/g, "-")
            .replace("&amp;", "")
            .replace("<p>", "")
            .replace("</p>", "");
          items.push(
            <BlogPostHeader
              key={index}
              id={sectionSubSubTitleId}
              className={sectionSubSubTitleStyle}
              color={sectionSubSubTitleColor}
              background={background}
              headerType="h4"
              copy={sectionSubSubTitle.copy}
              layoutWidth={layoutWidth}
              onClick={() => {
                copiedURLLink();
              }}
            />
          );
          break;
        case "copy":
          const copy = item as Copy;
          items.push(
            <Typography
              key={index}
              marginTop={margin}
              margin={"auto"}
              maxwidth={layoutWidth}
              className={copyStyle}
              color={copyColor}
              dangerouslySetInnerHTML
            >
              {copy.copy}
            </Typography>
          );
          break;
        case "quote":
          const quote = item as Quote;
          const quoteCopy = quote.copy.replace("<p>", "").replace("</p>", "");
          var source = "";
          if (quote.source) {
            source = quote.source.replace("<p>", "").replace("</p>", "");
          }

          items.push(
            <Typography
              key={index + "-quote"}
              marginTop={margin}
              margin={"auto"}
              maxwidth={600}
              textAlign="center"
              className={quoteStyle}
              color={quoteColor}
              dangerouslySetInnerHTML
            >
              {quoteCopy}
            </Typography>
          );
          items.push(
            <Typography
              key={index + "-source"}
              marginTop={gutter}
              textAlign="center"
              className={sourceStyle}
              color={sourceColor}
              dangerouslySetInnerHTML
            >
              {"- " + source}
            </Typography>
          );
          break;
        case "button":
          const button = item as ButtonType;
          items.push(
            <ButtonContainer
              key={index}
              margin={button.marginTop === "gutter" ? 0 : margin}
            >
              <TextButton page={button.page[0]} type={button.buttonType}>
                {button.buttonTitle}
              </TextButton>
            </ButtonContainer>
          );
          break;
        case "media":
          const media = item as MediaType;
          items.push(
            <Wrapper
              key={index}
              height={"auto"}
              marginTop={margin}
              maxWidth={imageWidth}
            >
              <Media hero={media.media} borderradius={20} />
              <Typography
                marginTop={margin}
                className={mediaSubtitleStyle}
                color={mediaSubtitleColor}
                textAlign={"center"}
                textWrap={"balance"}
                dangerouslySetInnerHTML
              >
                {media.subtitle}
              </Typography>
            </Wrapper>
          );
          break;
        case "markdown":
          const markdown = item as MarkdownType;
          const code = markdown.code
            .split("<p>")
            .join("")
            .split("</p>")
            .join("");
          items.push(
            <Wrapper
              as="code"
              key={index}
              height={"auto"}
              marginTop={margin}
              maxWidth={layoutWidth}
            >
              <Markdown children={code} />
            </Wrapper>
          );
          break;
        case "gist":
          const gist = item as GistType;
          items.push(
            <Wrapper
              key={index}
              height={"auto"}
              marginTop={margin}
              maxWidth={layoutWidth}
            >
              <Gist id={gist.reference} />
            </Wrapper>
          );
          break;
        default:
          break;
      }
    });
    return items;
  };

  export default BlogContentBlocks;